/**
 * MetricConditionControls — the metric kind's clause of a condition row:
 *   [ < ≤ = ≠ > ≥ ]  [ value ]  [⇄ compare]     (or the compare cluster)
 * Split out of RuleConditionRow (file cap). `allowCompare` off hides the ⇄
 * — a feature whose rules cannot compare two metrics (the Automation's)
 * shows the same row without it.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import type { FlowLayer, FlowOptimizerMetricMeta, RuleNode } from '@trafficpulse/shared';

import CompareSection from './compare/CompareSection';
import { metricLabel } from './metric-options';
import OperatorPills from './OperatorPills';
import ThresholdInput from './ThresholdInput';

type MetricNode = Extract<RuleNode, { kind: 'condition' }>;

export interface MetricConditionControlsProps {
  node: MetricNode;
  metrics: FlowOptimizerMetricMeta[];
  layer: FlowLayer;
  windowKey: string | null;
  allowCompare: boolean;
  onChange: (next: RuleNode) => void;
}

const MetricConditionControls: FC<MetricConditionControlsProps> = ({
  node,
  metrics,
  layer,
  windowKey,
  allowCompare,
  onChange,
}) => (
  <>
    <OperatorPills current={node.op} onChange={(op) => onChange({ ...node, op })} />
    {node.compareTo ? (
      <CompareSection
        node={node}
        metrics={metrics}
        layer={layer}
        windowKey={windowKey}
        onChange={onChange}
      />
    ) : (
      <>
        <ThresholdInput
          value={Number.isFinite(node.value) ? node.value : 0}
          onChange={(value) => onChange({ ...node, value })}
          ariaLabel={`Threshold for ${metricLabel(metrics, node.metric)}`}
        />
        {allowCompare ? (
          <button
            type="button"
            onClick={() => onChange({ ...node, compareTo: { metric: node.metric } })}
            title="Compare against another metric instead of a fixed value"
            aria-label="Compare against another metric"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </>
    )}
  </>
);

export default MetricConditionControls;
